import { Injectable, Type, isDevMode } from '@angular/core';

/** Maps designer/legacy module keys to the canonical registry module key. */
const MODULE_KEY_CANONICAL: Readonly<Record<string, string>> = {
  growth: 'growth-platform'
};

export interface WorkflowModuleSurfaceRegistration {
  surfaceKey: string;
  moduleKey: string;
  titleKey: string;
  descriptionKey?: string;
  loadComponent: () => Promise<Type<unknown>>;
  requiredContextKeys?: string[];
  fullPageRouteTemplate?: string;
}

@Injectable({ providedIn: 'root' })
export class WorkflowModuleSurfaceRegistry {
  private readonly entries = new Map<string, WorkflowModuleSurfaceRegistration>();

  register(entry: WorkflowModuleSurfaceRegistration): void {
    const moduleKey = entry.moduleKey.trim().toLowerCase();
    const surfaceKey = entry.surfaceKey.trim().toLowerCase();
    if (!moduleKey || !surfaceKey) {
      throw new Error('workflow_surface_registration_invalid');
    }

    this.entries.set(buildRegistryKey(moduleKey, surfaceKey), {
      ...entry,
      moduleKey,
      surfaceKey
    });
  }

  resolve(moduleKey: string, surfaceKey: string): WorkflowModuleSurfaceRegistration | null {
    const entry = this.lookupEntry(moduleKey, surfaceKey);
    if (!entry && isDevMode()) {
      console.warn(`[WorkflowModuleSurfaceRegistry] Missing registration for ${moduleKey}/${surfaceKey}`);
    }

    return entry;
  }

  listByModule(moduleKey: string): WorkflowModuleSurfaceRegistration[] {
    const normalized = moduleKey.trim().toLowerCase();
    const canonical = canonicalModuleKey(normalized);

    // Prefer canonical module registrations so alias pairs like growth/growth-platform do not
    // duplicate the same surfaceKey in designer dropdowns.
    const canonicalEntries = [...this.entries.values()].filter(item => item.moduleKey === canonical);
    if (canonicalEntries.length > 0) {
      return canonicalEntries;
    }

    if (canonical !== normalized) {
      return [...this.entries.values()].filter(item => item.moduleKey === normalized);
    }

    return [];
  }

  listAll(): WorkflowModuleSurfaceRegistration[] {
    return [...this.entries.values()].sort((a, b) => {
      const moduleCompare = a.moduleKey.localeCompare(b.moduleKey);
      return moduleCompare !== 0 ? moduleCompare : a.surfaceKey.localeCompare(b.surfaceKey);
    });
  }

  private lookupEntry(moduleKey: string, surfaceKey: string): WorkflowModuleSurfaceRegistration | null {
    const normalizedSurface = surfaceKey.trim().toLowerCase();
    for (const key of expandModuleKeyAliases(moduleKey)) {
      const entry = this.entries.get(buildRegistryKey(key, normalizedSurface));
      if (entry) {
        return entry;
      }
    }

    return null;
  }
}

function buildRegistryKey(moduleKey: string, surfaceKey: string): string {
  return `${moduleKey.trim().toLowerCase()}::${surfaceKey.trim().toLowerCase()}`;
}

function canonicalModuleKey(moduleKey: string): string {
  const normalized = moduleKey.trim().toLowerCase();
  return MODULE_KEY_CANONICAL[normalized] ?? normalized;
}

function expandModuleKeyAliases(moduleKey: string): Set<string> {
  const normalized = moduleKey.trim().toLowerCase();
  const canonical = canonicalModuleKey(normalized);
  return new Set([normalized, canonical]);
}
